import { useEffect, useState, useCallback } from "react";
import { Mail, MailOpen, Phone, AtSign, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ContactMessages() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    if (user && user.role !== "admin") return;
    api.get("/contact").then((r) => setItems(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const toggleRead = async (m) => {
    await api.patch(`/contact/${m.id}`, { read: !m.read });
    load();
  };

  if (user && !isAdmin) {
    return (
      <div data-testid="contact-denied" className="rounded-xl border border-slate-200 bg-white p-10 text-center">
        <ShieldAlert className="h-8 w-8 text-slate-300 mx-auto" />
        <h1 className="mt-3 font-heading text-xl font-bold tracking-tight">Access restricted</h1>
        <p className="text-slate-500 text-sm mt-1">Contact enquiries are only available to administrators.</p>
      </div>
    );
  }

  return (
    <div data-testid="contact-view">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Contact enquiries</h1>
      <p className="text-slate-500 mt-1">Messages submitted through the website contact form.</p>

      <div className="mt-6 space-y-3">
        {items.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400 text-sm">No enquiries yet.</div>}
        {items.map((m) => (
          <div key={m.id} data-testid="contact-row" className={`rounded-xl border bg-white p-5 flex items-start gap-4 transition-all ${m.read ? "border-slate-200" : "border-blue-200 bg-blue-50/40"}`}>
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${m.read ? "bg-slate-100 text-slate-400" : "bg-blue-100 text-blue-600"}`}>
              {m.read ? <MailOpen className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-slate-900">{m.name}</p>
                <a href={`mailto:${m.email}`} className="text-xs text-blue-600 flex items-center gap-1 hover:underline"><AtSign className="h-3 w-3" /> {m.email}</a>
                {m.phone && <span className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" /> {m.phone}</span>}
                {!m.read && <span className="rounded-full bg-blue-600 text-white text-xs font-semibold px-2 py-0.5">New</span>}
              </div>
              <p className="text-sm text-slate-600 mt-1">{m.message}</p>
              <p className="text-xs text-slate-400 mt-1">{new Date(m.created_at).toLocaleString()}</p>
            </div>
            <button data-testid="contact-toggle-read" onClick={() => toggleRead(m)} className="text-xs font-medium text-blue-600 hover:underline shrink-0">
              {m.read ? "Mark unread" : "Mark read"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
